import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsTextEntryModal from "../clients/Tools/kit/ToolsTextEntryModal";
import ToolsPageHeader from "../clients/Tools/kit/ToolsPageHeader";
import BuildProgressService from "../../services/BuildProgressService";
import SkillBuildService from "../../services/SkillBuildService";
import UtilityService from "../../services/UtilityService";
import AgentSourceIcon from "./AgentSourceIcon";
import BuildConversation from "./BuildConversation";
import BuildBrief from "./BuildBrief";
import BuildPageSkeleton from "./BuildPageSkeleton";

import "./build-page.css";

class SkillBuildPage extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			session: null,
			loadError: null,
			draftText: "",
			isSending: false,
			waitStartedAt: null,
			actionError: null,
			isPasteOpen: false,
			pastedProcedure: "",
		};
	}

	componentDidMount() {
		this.fetchSession();
	}

	fetchSession = async () => {
		const { sessionId } = this.props;
		try {
			const session = await SkillBuildService.getSkillBuild({ sessionId });
			await this.update({ session, loadError: null });
		} catch (error) {
			await this.update({ loadError: UtilityService.describeRequestError({ error, fallback: "This conversation could not be loaded." }) });
		}
	};

	act = async ({ action }) => {
		await this.update({ isSending: true, actionError: null });
		try {
			const session = await action();
			await this.update({ session, isSending: false });
			return true;
		} catch (error) {
			await this.update({ isSending: false, actionError: UtilityService.describeRequestError({ error, fallback: "That did not work. Try again." }) });
			return false;
		}
	};

	onSend = async () => {
		const { sessionId } = this.props;
		const text = this.state.draftText.trim();
		if (!text) return;
		const pendingSession = { ...this.state.session, messages: [...this.state.session.messages, { from: "person", text, at: new Date().toISOString() }] };
		await this.update({ draftText: "", session: pendingSession, waitStartedAt: Date.now() });
		const isSent = await this.act({ action: () => SkillBuildService.sendSkillBuildMessage({ sessionId, text }) });
		await this.update({ waitStartedAt: null });
		if (!isSent) await this.update({ draftText: text, session: { ...pendingSession, messages: pendingSession.messages.slice(0, -1) } });
	};

	onImportProcedure = async () => {
		const { sessionId } = this.props;
		const procedure = this.state.pastedProcedure.trim();
		await this.update({ isPasteOpen: false, waitStartedAt: Date.now() });
		const isImported = await this.act({ action: () => SkillBuildService.importSkillBuildProcedure({ sessionId, procedure }) });
		await this.update({ waitStartedAt: null });
		if (isImported) await this.update({ pastedProcedure: "" });
	};

	renderDraftTool = tool => {
		return (
			<span key={tool.name} className="tk-chip">
				{tool.needs && <AgentSourceIcon icon={tool.needs} size="small" />}
				{tool.label}
				{tool.isReadFirst && <span className="tk-chip__note">read first</span>}
			</span>
		);
	};

	renderDraft() {
		const { onReviewDraft, onOpenSkill } = this.props;
		const { session, isSending } = this.state;
		const { draftSkill } = session;
		const isOpen = session.status === "open";
		const isSaved = !!session.savedSkillId;

		return (
			<section className="build-card build-card--draft tk-card">
				<div className="build-card__head">
					<span className="build-card__title">Draft</span>
					{isSaved && <span className="tk-badge tk-badge--success">Saved</span>}
					{!isSaved && draftSkill && <span className="tk-badge tk-badge--accent">Drafted</span>}
				</div>
				{!draftSkill && (
					<div className="tk-muted">Keep talking with the guide. It drafts the skill once it has the purpose, the steps and the checks.</div>
				)}
				{draftSkill && (
					<React.Fragment>
						<div className="skill-build__draft-summary">
							<strong>{draftSkill.name}</strong>
							<span>{draftSkill.description}</span>
						</div>
						{draftSkill.tools.length > 0 && (
							<div className="tk-chips">
								<span className="tk-chips__label">Brings</span>
								{draftSkill.tools.map(this.renderDraftTool)}
							</div>
						)}
						<pre className="build-card__text">{draftSkill.body}</pre>
					</React.Fragment>
				)}
				{isOpen && draftSkill && (
					<React.Fragment>
						<button type="button" className="tk-button tk-button--primary" onClick={() => onReviewDraft({ session })} disabled={isSending}>
							<Icon.Check size={14} />
							Review and save
						</button>
						<span className="tk-muted">You check it in the editor first; nothing is saved until you do.</span>
					</React.Fragment>
				)}
				{isSaved && (
					<button
						type="button"
						className="tk-button tk-button--secondary tk-button--compact"
						onClick={() => onOpenSkill({ skillId: session.savedSkillId })}
					>
						<Icon.BookOpen size={13} />
						Open the skill
					</button>
				)}
			</section>
		);
	}

	renderPasteModal() {
		const { isPasteOpen, pastedProcedure } = this.state;
		return (
			<ToolsTextEntryModal
				isOpen={isPasteOpen}
				title="Paste a procedure you already have"
				width={680}
				intro="From a checklist, a policy or your own notes. The guide reads it, fills in the brief in your words and asks only what it leaves open. It stays with this conversation and is not shown to anyone else."
				text={pastedProcedure}
				rows={14}
				confirmLabel="Use this procedure"
				onTextChange={({ text }) => this.update({ pastedProcedure: text })}
				onCancel={() => this.update({ isPasteOpen: false })}
				onConfirm={this.onImportProcedure}
			/>
		);
	}

	render() {
		const { onBack, onWriteByHand } = this.props;
		const { session, loadError, actionError, draftText, isSending, waitStartedAt } = this.state;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;
		if (!session) return <BuildPageSkeleton hasPanelTabs={false} />;
		const isOpen = session.status === "open";
		const isGuideWaiting = isSending && !!waitStartedAt;
		const progress = isGuideWaiting
			? {
					steps: BuildProgressService.skillTurnSteps({ session }),
					slowAfterSeconds: BuildProgressService.SKILL_TURN_SLOW_AFTER_SECONDS,
					startedAt: waitStartedAt,
					longestSeconds: BuildProgressService.SKILL_TURN_LONGEST_SECONDS,
			  }
			: null;
		const title = session.draftSkill ? session.draftSkill.name : session.brief.name || "New skill";

		return (
			<div className="build-page">
				<ToolsPageHeader
					compact
					onBack={onBack}
					backLabel="Back to skills"
					lead={
						<span className="tk-icon-tile tk-icon-tile--lg">
							<Icon.Edit3 />
						</span>
					}
					title={title}
					badges={session.savedSkillId ? <span className="tk-badge tk-badge--success">Saved</span> : null}
					subtitle="Writing with the skill guide. It drafts the skill from what you tell it; you review and save it."
				/>
				{actionError && (
					<div className="tk-notice tk-notice--danger">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{actionError}
						</div>
					</div>
				)}
				<div className="build-page__layout">
					<BuildConversation
						messages={session.messages}
						isSending={isSending}
						progress={progress}
						isOpen={isOpen}
						draftText={draftText}
						onDraftTextChange={({ text }) => this.update({ draftText: text })}
						onSend={this.onSend}
						secondaryAction={
							<button
								type="button"
								className="tk-text-action tk-text-action--muted"
								onClick={() => this.update({ isPasteOpen: true })}
								disabled={isSending}
							>
								<Icon.Clipboard size={13} />
								Paste a procedure you already have
							</button>
						}
					/>
					<div className="build-page__side">
						{this.renderDraft()}
						<BuildBrief fields={SkillBuildService.skillBriefFields()} brief={session.brief} category={session.category} />
						<section className="build-card tk-card">
							<div className="build-card__guided-by">
								Guided by {session.guidingSkills.map(skill => `${skill.name} v${skill.versionNumber}`).join(" · ")}
							</div>
							{isOpen && (
								<button
									type="button"
									className="tk-text-action tk-text-action--muted"
									onClick={() => onWriteByHand({ categoryId: session.category ? session.category.id : null })}
								>
									Write it by hand instead
								</button>
							)}
						</section>
					</div>
				</div>
				{this.renderPasteModal()}
			</div>
		);
	}
}

export default SkillBuildPage;
